import {
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSavedFilter } from '../queryKeys';

import { fetchSavedFilters } from './fetchSavedFilters';
import {
  type QuerySavedFiltersResponse,
  type SavedFiltersQueryParams,
} from './types';

/**
 * Generates query options for the saved-filter list query.
 *
 * @param queryParams - Saved-filter list query parameters.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and staleTime.
 */
// fallow-ignore-next-line unused-export
export const getSavedFiltersQueryOptions = <TData = QuerySavedFiltersResponse>({
  queryParams,
  options,
}: {
  queryParams: SavedFiltersQueryParams;
  options?: Omit<
    UseQueryOptions<QuerySavedFiltersResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) => {
  const { type } = queryParams;

  return queryOptions({
    queryFn: async ({ signal }) => fetchSavedFilters({ type }, { signal }),
    queryKey: queryKeysSavedFilter.getAll(type),
    staleTime: ms('60s'),
    // A saved-filter failure stays local: unavailable filter tabs must not take
    // down the list they sit above.
    throwOnError: false,
    ...options,
  });
};

/**
 * React Query based custom hook for fetching saved filters by type.
 *
 * @param queryParams - Saved-filter list query parameters.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook containing saved filters.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/list_saved_filters_user_saved_filters_get
 */
export const useQuerySavedFilters = <TData = QuerySavedFiltersResponse>(
  queryParams: SavedFiltersQueryParams,
  options?: Omit<
    UseQueryOptions<QuerySavedFiltersResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getSavedFiltersQueryOptions<TData>({ queryParams, options }));
